import {
  NormalizedBizError,
  NormalizedError
} from '@/types/axios/public-json-structure'
import axios, { AxiosError, Cancel } from 'axios'

import globalErrorCode from '@/const/errorCode'
import { CustomizeInterceptor } from '@/types/axios/interceptor'
import { IS_DEV } from '@/const'

const globalErrorCodeKeys = Object.keys(
  globalErrorCode
) as (keyof typeof globalErrorCode)[]

/**
 * 前端自定义错误码
 * 用字符串方式记录, 便于在日志中直接判断异常类型
 * 如果用纯数字方式记录, 还需再到这里对应关系
 */
export const requestErrorCodeMap: Record<
keyof typeof globalErrorCode | 'TIME_OUT' | 'NETWORK_ERROR' | 'UNKNOWN',
string
> = {
  401: 'UN_AUTH 401',
  403: 'FORBIDDEN 403',
  404: 'NOT_FOUND 404',
  default: 'UNKNOWN',
  UNKNOWN: 'UNKNOWN',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIME_OUT: 'TIME_OUT'
}

const interceptor: CustomizeInterceptor<any, Promise<never>> = {
  fulfilled: undefined,
  rejected: (error: AxiosError | NormalizedBizError | Cancel) => {
    if (IS_DEV) {
      console.log('axios 请求异常. response 拦截器中捕获异常:', error) // for debug
    }

    let normalizedError: NormalizedError

    /**
     * 如果取消了请求
     * 也就是通过cancelToken中止了某次请求
     */
    if (
      axios.isCancel(error) // TODO: 这里怎样让TS认为error的类型是Cancel？
    ) {
      normalizedError = {
        errorType: 'cancel',
        message: (error as Cancel).message ?? '请求已取消',
        messageHasShown: false
      }
    } else if ('isAxiosError' in error) {
      /**
       * 如果axios内部进行了报错
       * 1. 网络错误
       * 2. axios实例 validateStatus 函数调用后返回了false
       */
      let message = ''
      let errorCode
      const err = error as any

      if (err.code === 'ECONNABORTED') {
        // timeout error code: https://stackoverflow.com/a/50620317
        message = '请求失败， 服务器未返回内容，请稍后再试'
        errorCode = requestErrorCodeMap.TIME_OUT
      } else if (err.message === 'Network Error') {
        // 网络错误, 比如和服务端无法建立连接
        message = '请求失败， 请检查网络连接'
        errorCode = requestErrorCodeMap.NETWORK_ERROR
      } else {
        const status = err.response?.status ?? -999

        if (
          globalErrorCodeKeys.includes(status as keyof typeof globalErrorCode)
        ) {
          errorCode =
            requestErrorCodeMap[status as keyof typeof globalErrorCode]
          message = globalErrorCode[status as keyof typeof globalErrorCode]
        } else {
          errorCode = err.code ?? requestErrorCodeMap.UNKNOWN
          message = err.message
        }
      }

      normalizedError = {
        axiosError: error,
        code: errorCode,
        errorType: 'axios',
        message,
        httpStatus: err.response?.status,
        messageHasShown: false
      }
    } else {
      /**
       * 业务上的报错
       */
      normalizedError = error as NormalizedBizError
    }

    return Promise.reject(normalizedError)
  }
}

export default interceptor
